import React from 'react';
import type { DashboardAgentNetworkData } from '../../../types';
import MetricStrip from './MetricStrip';
import TabSection from './TabSection';
import ChartCard from './charts/ChartCard';
import DonutChart from './charts/DonutChart';
import GaugeChart from './charts/GaugeChart';
import RankedBarChart from './charts/RankedBarChart';
import StackedBarChart from './charts/StackedBarChart';
import TrendLineChart from './charts/TrendLineChart';
import { BLUE_RAMP, CHART_COLORS, formatUsdFull } from './charts/common';
import { isCompositionEmpty, isSeriesEmpty, toBucketSeries } from './executiveModel';

interface AgentNetworkTabProps {
  data: DashboardAgentNetworkData | undefined;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
}

const TIER_SERIES = [
  { dataKey: 'superAgent', name: 'Super agents', color: BLUE_RAMP[0] },
  { dataKey: 'agent', name: 'Agents', color: BLUE_RAMP[2] },
  { dataKey: 'shop', name: 'Shops', color: BLUE_RAMP[4] },
];

// Received is a subset of expected, not an addition to it — side by side, and
// the shortfall is the visible gap between the pair.
const SETTLEMENT_SERIES = [
  { dataKey: 'expected', name: 'Cash expected', color: BLUE_RAMP[3], stackId: 'expected' },
  { dataKey: 'received', name: 'Cash received', color: BLUE_RAMP[0], stackId: 'received' },
];

/** Tier economics — what the network buys, what the platform earns, where the risk sits. */
const AgentNetworkTab: React.FC<AgentNetworkTabProps> = ({ data, isLoading, error, onRetry }) => {
  const purchases = toBucketSeries(data?.charts?.purchasesByTier, ['superAgent', 'agent', 'shop']);
  const settlement = toBucketSeries(data?.charts?.cashSettlement, ['expected', 'received']);
  const credits = toBucketSeries(data?.charts?.credits, ['credits']);
  const bucketNote = data?.charts?.granularity === 'hour' ? 'Hourly buckets' : 'Daily buckets';

  const topAgents = (data?.charts?.topAgents ?? []).map((a) => ({
    name: a.name,
    value: a.creditsPurchased,
  }));
  const topShops = (data?.charts?.topShops ?? []).map((s) => ({
    name: s.name,
    value: s.creditsPurchased,
  }));

  const tierMix = data
    ? [
        { name: 'Super agents', value: Math.max(0, data.kpis.creditPurchasesSuperAgent.value) },
        { name: 'Agents', value: Math.max(0, data.kpis.creditPurchasesAgent.value) },
        { name: 'Shops', value: Math.max(0, data.kpis.creditPurchasesShop.value) },
      ].filter((t) => t.value > 0)
    : [];

  const expected = data?.kpis.cashExpected.value ?? 0;
  const received = data?.kpis.cashReceived.value ?? 0;
  const collectedPct =
    expected > 0 ? Math.min(100, Math.max(0, (received / expected) * 100)) : received > 0 ? 100 : 0;

  return (
    <TabSection isLoading={isLoading} error={error} onRetry={onRetry}>
      {data && (
        <div className="space-y-4">
          <MetricStrip
            title="Credit sales"
            columnsClassName="grid-cols-2 sm:grid-cols-3 xl:grid-cols-6"
            items={[
              { metric: 'creditPurchasesTotal', kpi: data.kpis.creditPurchasesTotal, format: 'currency', emphasis: true },
              { metric: 'creditPurchasesSuperAgent', kpi: data.kpis.creditPurchasesSuperAgent, format: 'currency' },
              { metric: 'creditPurchasesAgent', kpi: data.kpis.creditPurchasesAgent, format: 'currency' },
              { metric: 'creditPurchasesShop', kpi: data.kpis.creditPurchasesShop, format: 'currency' },
              { metric: 'creditsIssued', kpi: data.kpis.creditsIssued, format: 'number' },
              { metric: 'platformFeeRevenue', kpi: data.kpis.platformFeeRevenue, format: 'currency' },
            ]}
          />

          <MetricStrip
            title="Cash & risk"
            columnsClassName="grid-cols-2 sm:grid-cols-3 xl:grid-cols-5"
            items={[
              { metric: 'cashExpected', kpi: data.kpis.cashExpected, format: 'currency' },
              { metric: 'cashReceived', kpi: data.kpis.cashReceived, format: 'currency' },
              { metric: 'cashUncollected', kpi: data.kpis.cashUncollected, format: 'currency', invertDelta: true },
              { metric: 'onlineDebtOutstanding', kpi: data.kpis.onlineDebtOutstanding, format: 'currency', invertDelta: true },
              { metric: 'tierUsdFloatTotal', kpi: data.kpis.tierUsdFloatTotal, format: 'currency' },
            ]}
          />

          <div className="grid gap-3 lg:grid-cols-3">
            <ChartCard
              metric="purchasesByTierTrend"
              subtitle={bucketNote}
              className="lg:col-span-2"
              isEmpty={isSeriesEmpty(purchases, ['superAgent', 'agent', 'shop'])}
            >
              <StackedBarChart
                data={purchases}
                series={TIER_SERIES}
                xKey="label"
                labelFormatter={(v) => v}
              />
            </ChartCard>

            <ChartCard metric="creditSalesByTier" isEmpty={isCompositionEmpty(tierMix)}>
              <DonutChart
                data={tierMix}
                centerLabel="Purchases"
                centerValue={formatUsdFull(data.kpis.creditPurchasesTotal.value)}
              />
            </ChartCard>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <ChartCard
              metric="topAgentsByPurchases"
              isEmpty={topAgents.length === 0}
              emptyMessage="No agent purchases in this period"
            >
              <RankedBarChart data={topAgents} valueLabel="Purchases" />
            </ChartCard>
            <ChartCard
              metric="topShopsByPurchases"
              isEmpty={topShops.length === 0}
              emptyMessage="No shop purchases in this period"
            >
              <RankedBarChart data={topShops} valueLabel="Purchases" />
            </ChartCard>
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <ChartCard
              metric="cashSettlementTrend"
              subtitle={bucketNote}
              className="lg:col-span-2"
              isEmpty={isSeriesEmpty(settlement, ['expected', 'received'])}
              emptyMessage="No cash settlements in this period"
            >
              <StackedBarChart
                data={settlement}
                series={SETTLEMENT_SERIES}
                xKey="label"
                labelFormatter={(v) => v}
              />
            </ChartCard>

            <ChartCard
              metric="cashSettlementHealth"
              isEmpty={expected === 0 && received === 0}
              emptyMessage="No cash settlements in this period"
            >
              <GaugeChart
                percent={Math.round(collectedPct * 10) / 10}
                primaryLabel="Cash received"
                primaryValue={received}
                secondaryLabel="Still uncollected"
                secondaryValue={Math.max(0, data.kpis.cashUncollected.value)}
              />
            </ChartCard>
          </div>

          <ChartCard
            metric="creditsIssuedTrend"
            subtitle={bucketNote}
            isEmpty={isSeriesEmpty(credits, ['credits'])}
          >
            <TrendLineChart
              data={credits}
              dataKey="credits"
              name="Credits issued"
              xKey="label"
              labelFormatter={(v) => v}
              color={CHART_COLORS.credits}
            />
          </ChartCard>
        </div>
      )}
    </TabSection>
  );
};

export default AgentNetworkTab;
